import { useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useState, useCallback } from "react";

import NotificationAlertOptions from "@/layouts/Alerts";
import { updateSettingVariables } from "@/services/siteServices/siteActions";
import { useCurrentPermission } from "@/services/userServices/useCurrentPermission";
import AddSiteConfigurationsView from "./AddSiteConfigurationsView";
// Helper: for ignoring setState useless
const deepEqual = (a: unknown, b: unknown) => {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
};

// SCRUM-1225: SettingsRequired / SettingsOptional were accepted here and never
// read — a new setting is free text (`newSetting.variable`) with its type from
// the hardcoded dropdown below, so the design-level catalogue constrained nothing.
/** One entry of the site's `settings_variables`. */
type SettingEntry = {
  variable?: string;
  type?: string;
  value?: unknown;
  complextype?: string;
};

/** The setting being typed into the Add form. */
type NewSetting = {
  variable?: string;
  type?: string;
  value?: string;
  complextype?: string;
};

type AddSiteConfigurationsContainerProps = {
  SettingsVariables?: SettingEntry[];
  current?: string;
  action?: string;
};

const AddSiteConfigurationsContainer = ({
  SettingsVariables = [],
  current = "",
  action = "add",
}: AddSiteConfigurationsContainerProps) => {
  const dispatch = useAppDispatch();
  const permission = useCurrentPermission();

  // Liste des types
  const dropdownList = useMemo(
    () => [
      { label: "String", value: "String" },
      { label: "Number", value: "Number" },
      { label: "Boolean", value: "Boolean" },
    ],
    []
  );


  const [newSetting, setNewSetting] = useState<NewSetting>({ type: "String" });
  const [localSettingsVariables, setLocalSettingsVariables] = useState<SettingEntry[]>(
    Array.isArray(SettingsVariables) ? SettingsVariables : []
  );


  useEffect(() => {
    if (
      Array.isArray(SettingsVariables) &&
      !deepEqual(SettingsVariables, localSettingsVariables)
    ) {
      setLocalSettingsVariables(SettingsVariables);
    }
  }, [SettingsVariables, localSettingsVariables]);

  
  const onChangeSettingRequired = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSetting((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeSettingsOptional = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewSetting((prev) => ({ ...prev, [name]: value }));
  }, []);

  const onChangeOptions = useCallback((opt: { value: string } | null, name: string) => {
    setNewSetting((prev) => ({ ...prev, [name]: opt?.value }));
  }, []);

  const onClickAddSettings = useCallback(() => {
    if (!newSetting.variable || !newSetting.variable.trim()) {
      NotificationAlertOptions("error", "Settings Name is Required!");
      return;
    }

    const newSettingObj = {
      variable: newSetting.variable || "",
      type: newSetting.type || "",
      value: newSetting.value || "",
      complextype: newSetting.complextype || "",
    };

    const settings_variables = [...localSettingsVariables, newSettingObj];

 
    dispatch(updateSettingVariables({ settings_variables }));

 
    NotificationAlertOptions("success", "Configuration added with success");
    setNewSetting({ type: "String", variable: "", value: "", complextype: "" });

   
    window.location.href = "/admin/site-configuration";
  }, [dispatch, newSetting, localSettingsVariables]);

  const canSubmit = permission.publish || permission.edit;

  return (
    <AddSiteConfigurationsView
      current={current}
      newSetting={newSetting}
      dropdownList={dropdownList}
      onChangeSettingRequired={onChangeSettingRequired}
      onChangeSettingsOptional={onChangeSettingsOptional}
      onChangeOptions={onChangeOptions}
      onClickAddSettings={onClickAddSettings}
      canSubmit={canSubmit}
    />
  );
};

export default AddSiteConfigurationsContainer;
